<script setup lang="ts">
  import { computed } from 'vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import { hiddenInBranch } from '@/domain/catalog/catalog-filters';
  import { normalizeText } from '@/domain/pos/search-normalize';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  /**
   * The categories as a table of contents (plan §5.7): name, product count
   * (matches while filtering), POS-only icon and a hollow dot when hidden.
   * Used by the rail and by the phone category sheet.
   */
  const props = withDefaults(defineProps<{ query?: string }>(), {
    query: '',
  });

  const emit = defineEmits<{ (e: 'jump', categoryId: number): void }>();

  const { state, data } = useCatalogPage();
  const restaurantStore = useRestaurantStore();
  const branchName = computed(() => restaurantStore.selectedBranch?.name ?? '');

  const rows = computed(() => {
    const text = normalizeText(props.query);
    const filtering = data.isFiltering.value;
    const branchId = data.branchId.value;
    return data.shownSections.value
      .filter(
        (section) =>
          !text || normalizeText(section.category.name).includes(text)
      )
      .map((section) => ({
        id: section.category.id,
        name: section.category.name,
        count: filtering ? section.matchCount : section.items.length,
        posOnly: section.category.posOnly,
        hidden: !section.category.visible
          ? 'everywhere'
          : hiddenInBranch(section.category, branchId)
          ? 'here'
          : null,
      }));
  });
</script>

<template>
  <nav
    class="v2-catalog-category-nav-list"
    :aria-label="$t('menuPage.nav.label')"
  >
    <button
      v-for="row in rows"
      :key="row.id"
      type="button"
      class="nav-row"
      :class="{ 'is-active': row.id === state.activeCategoryId }"
      :aria-current="row.id === state.activeCategoryId ? 'true' : undefined"
      :data-nav-id="row.id"
      @click="emit('jump', row.id)"
    >
      <span class="row-name" :title="row.name">{{ row.name }}</span>
      <i
        v-if="row.posOnly"
        class="mdi mdi-cash-register row-pos"
        :title="$t('menuPage.card.posOnly')"
      />
      <span
        v-if="row.hidden"
        class="row-hidden"
        :title="
          row.hidden === 'here'
            ? $t('menuPage.categoryStatus.hiddenHere', { branch: branchName })
            : $t('menuPage.categoryStatus.hidden')
        "
      />
      <span class="row-count">{{ row.count }}</span>
    </button>
    <p v-if="!rows.length && query.trim()" class="nav-empty">
      {{ $t('menuPage.move.noResults') }}
    </p>
  </nav>
</template>

<style scoped lang="scss">
  .v2-catalog-category-nav-list {
    @apply tw-flex tw-flex-col;

    .nav-row {
      @apply tw-relative tw-flex tw-h-10 tw-w-full tw-flex-none tw-cursor-pointer tw-items-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-px-3 tw-text-left tw-text-sm tw-text-neutral-700 tw-transition-colors tw-duration-150;

      &:hover {
        @apply tw-bg-neutral-900/5;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }

      // The «you are here» pill: white, hairline, a dark marker at the left.
      &.is-active {
        @apply tw-bg-surface tw-font-semibold tw-text-neutral-900;
        box-shadow: inset 0 0 0 1px rgb(var(--c-neutral-200)),
          0 1px 2px 0 rgb(var(--c-neutral-900) / 0.05);

        &::before {
          @apply tw-absolute tw-bottom-2 tw-left-0 tw-top-2 tw-w-0.5 tw-rounded-full tw-bg-neutral-900;
          content: '';
        }
      }

      .row-name {
        @apply tw-min-w-0 tw-flex-1 tw-truncate;
      }

      .row-pos {
        @apply tw-ml-1.5 tw-flex-none tw-text-base tw-leading-none tw-text-sky-700;
      }

      .row-hidden {
        @apply tw-ml-1.5 tw-h-[7px] tw-w-[7px] tw-flex-none tw-rounded-full tw-border-[1.5px] tw-border-solid tw-border-neutral-400;
      }

      .row-count {
        @apply tw-ml-2 tw-flex-none tw-text-[13px] tw-font-normal tw-tabular-nums tw-text-neutral-500;
      }
    }

    .nav-empty {
      @apply tw-m-0 tw-px-3 tw-py-3 tw-text-sm tw-text-neutral-500;
    }
  }
</style>
